import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { FlipCard } from './FlipCard';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api.service';
import { Activity } from '../types/activity';
import {
    ArrowLeft,
    Calendar,
    MapPin,
    Users,
    Star,
    Heart,
    HeartOff,
    CheckCircle,
    ExternalLink,
    Share2,
    Copy,
    Award,
    TrendingUp,
    Clock
} from 'lucide-react';


export function CreatorProfilePage() {
    const { creatorId } = useParams<{ creatorId: string }>();
    const navigate = useNavigate();
    const [creator, setCreator] = useState<any>(null);
    const [isFollowing, setIsFollowing] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [activities, setActivities] = useState<Activity[]>([]);
    const { user, isAuthenticated } = useAuth();

    const fetchCreator = async (creatorId: string) => {
        const decodedId = decodeURIComponent(creatorId);
        const creator = await apiService.getCreatorById(decodedId);
        setCreator(creator);
    }

    const fetchRecentActivities = async () => {
        const activitiesByCreatorId = await apiService.getActivitiesByCreatorId(creatorId || '');
        setActivities(activitiesByCreatorId);
    }

    useEffect(() => {
        if (creatorId) {
            fetchCreator(creatorId);
            fetchRecentActivities();

            // Mock: Check if user is following this organizer
            // setIsFollowing(['Zen Studios', 'Tennis Club NYC', 'Adventure Gym', 'Bike Enthusiasts'].includes(decodedName));
            setIsFollowing(false)
        }
    }, [creatorId]);

    if (!creator) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center py-12">
                    <h1 className="text-2xl font-bold mb-4">Organizer Not Found</h1>
                    <p className="text-muted-foreground mb-6">
                        The organizer profile you're looking for doesn't exist.
                    </p>
                    <Button onClick={() => navigate('/explore')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Explore
                    </Button>
                </div>
            </div>
        );
    }

    const handleFollowToggle = () => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        setIsFollowing(!isFollowing);
        console.log(isFollowing ? `Unfollowed ${creator.name}` : `Following ${creator.name}`);
    };

    const handleShare = async () => {
        const url = window.location.href;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: `${creator.name} - Activity Organizer`,
                    text: `Check out ${creator.name}, a ${creator.type} on our platform`,
                    url: url,
                });
            } catch (err) {
                handleCopyUrl();
            }
        } else {
            handleCopyUrl();
        }
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.log('Copy failed');
        }
    };

    const handleActivityClick = (activityId: string) => {
        navigate(`/activity/${activityId}`);
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Back Button - Fixed Position */}
            <div className="relative z-10 p-4">
                <Button
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    className="flex items-center space-x-2 bg-background/80 backdrop-blur-sm"
                >
                    <ArrowLeft size={16} />
                    <span>Back</span>
                </Button>
            </div>

            {/* Hero Section */}
            <div className="relative -mt-16">
                <div className="h-80 overflow-hidden">
                    <img
                        // src={creator.coverImage}
                        src="https://playhub.sfo3.cdn.digitaloceanspaces.com/activity_image/user_profile_backgound.jpg"
                        alt={`${creator.name} cover`}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
                </div>

                {/* Profile Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-end justify-between">
                            <div className="flex items-end space-x-6">
                                <Avatar className="w-24 h-24 border-4 border-background">
                                    <AvatarImage src={creator.avatar} alt={creator.name} />
                                    <AvatarFallback className="text-lg">
                                        {creator.name.slice(0, 2).toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="pb-2">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <h1 className="text-3xl font-bold text-white">{creator.name}</h1>
                                        {creator.isVerified && (
                                            <CheckCircle className="w-7 h-7 text-blue-500 fill-current" />
                                        )}
                                    </div>
                                    <p className="text-xl text-white/90 mb-1">{creator.type}</p>
                                    <div className="flex items-center space-x-4 text-white/80 text-sm">
                                        <div className="flex items-center space-x-1">
                                            <MapPin size={16} />
                                            <span>{creator.location || 'Somewhere'}, {creator.state || 'On Earth'}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                            {/* <span>{organizer.stats.rating} ({organizer.stats.reviewCount} reviews)</span> */}
                                            {/* <span>{123} ({123} reviews)</span> */}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center space-x-3">
                                <Button
                                    onClick={handleShare}
                                    variant="secondary"
                                    size="sm"
                                    className="flex items-center space-x-2"
                                >
                                    {copySuccess ? (
                                        <>
                                            <CheckCircle size={16} />
                                            <span>Copied!</span>
                                        </>
                                    ) : (
                                        <>
                                            <Share2 size={16} />
                                            <span>Share</span>
                                        </>
                                    )}
                                </Button>

                                <Button
                                    onClick={handleFollowToggle}
                                    variant={isFollowing ? "secondary" : "default"}
                                    className="flex items-center space-x-2"
                                >
                                    {isFollowing ? (
                                        <>
                                            <Heart className="w-4 h-4 fill-current" />
                                            <span>Following</span>
                                        </>
                                    ) : (
                                        <>
                                            <HeartOff className="w-4 h-4" />
                                            <span>Follow</span>
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <Users className="w-6 h-6 mx-auto mb-2 text-primary" />
                            <p className="text-2xl font-bold">{123}</p>
                            <p className="text-xs text-muted-foreground">Followers</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <Calendar className="w-6 h-6 mx-auto mb-2 text-primary" />
                            <p className="text-2xl font-bold">{activities.length}</p>
                            <p className="text-xs text-muted-foreground">Activities</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-green-500" />
                            <p className="text-2xl font-bold">{123}%</p>
                            <p className="text-xs text-muted-foreground">Completion</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <Star className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
                            <p className="text-2xl font-bold">{123}</p>
                            <p className="text-xs text-muted-foreground">Rating</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabbed Content */}
                <Tabs defaultValue="about" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="about">About</TabsTrigger>
                        <TabsTrigger value="activities">Past Activities</TabsTrigger>
                        <TabsTrigger value="achievements">Achievements</TabsTrigger>
                    </TabsList>

                    {/* About Tab */}
                    <TabsContent value="about" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>About {creator.name}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-muted-foreground leading-relaxed">
                                    {creator.description || 'This person is too lazy to write a description...'}
                                </p>

                                <Separator />

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="font-semibold mb-2">Contact Information</h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center space-x-2">
                                                <ExternalLink className="w-4 h-4 text-muted-foreground" />
                                                <a
                                                    href={`https://${creator.website}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline"
                                                >
                                                    {creator.website || 'No website available'}
                                                </a>
                                            </div>
                                            {creator.email && (
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-muted-foreground">Email:</span>
                                                    <a href={`mailto:${creator.email}`} className="text-primary hover:underline">
                                                        {creator.email}
                                                    </a>
                                                </div>
                                            )}
                                            {creator.phone && (
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-muted-foreground">Phone:</span>
                                                    <span>{creator.phone}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <h4 className="font-semibold mb-2">Specialties</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {['dummy specialty1', 'dummy specialty2', 'dummy specialty3'].map((specialty: string) => (
                                                <Badge key={specialty} variant="secondary" className="text-xs">
                                                    {specialty}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="text-center text-xs text-muted-foreground pt-4 border-t">
                                    Member since {new Date(creator.createdAt).toLocaleDateString('en-US', {
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Past Activities Tab */}
                    <TabsContent value="activities" className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Past Activities</h3>
                            <Badge variant="outline">{123} completed</Badge>
                        </div>

                        {activities.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {activities.map((activity: any) => (
                                    <Card key={activity.id} className="cursor-pointer hover:shadow-lg transition-shadow">
                                        <div onClick={() => handleActivityClick(activity.id)}>
                                            <div className="aspect-video relative overflow-hidden rounded-t-lg">
                                                <img
                                                    src={activity.image}
                                                    alt={activity.title}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute top-3 right-3">
                                                    <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm">
                                                        {activity.type}
                                                    </Badge>
                                                </div>
                                            </div>
                                            <CardContent className="p-4">
                                                <div className="flex items-start justify-between mb-2">
                                                    <h4 className="font-semibold">{activity.title}</h4>
                                                    <div className="flex items-center space-x-1 text-xs">
                                                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                                        <span>{activity.rating}</span>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-muted-foreground mb-3">{activity.description}</p>
                                                <div className="space-y-2 text-xs text-muted-foreground">
                                                    <div className="flex items-center space-x-2">
                                                        <Calendar className="w-3 h-3" />
                                                        <span>{new Date(activity.dateTime).toLocaleDateString()} at {new Date(activity.dateTime).toLocaleTimeString()}</span>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <Users className="w-3 h-3" />
                                                        <span>{activity.participants}/{activity.maxParticipants} participants</span>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <MapPin className="w-3 h-3" />
                                                        <span>{activity.location}</span>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <Clock className="mx-auto mb-4 text-muted-foreground" size={48} />
                                <p className="text-muted-foreground">No past activities to display yet.</p>
                            </div>
                        )}
                    </TabsContent>

                    {/* Achievements Tab */}
                    <TabsContent value="achievements" className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Achievements & Certifications</h3>
                            <Badge variant="outline">{123} achievements</Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {['dummy achievement1', 'dummy achievement2', 'dummy achievement3'].map((achievement: any, index: number) => (
                                <Card key={index}>
                                    <CardContent className="p-4">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                                <Award className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold">{achievement.title || 'Dummy Achievement'}</h4>
                                                <p className="text-sm text-muted-foreground">Earned in {achievement.year || '2025'}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}